/**
 * Auth Integration Service
 * 
 * Connects secure token storage with the existing authentication and permissions system.
 * Provides seamless integration between offline token management and online auth flows.
 */

import { secureTokenStorage, type StoredTokenData } from './SecureTokenStorage';
import { tokenRotationService } from './TokenRotationService';
import { secureStorageFallbacks } from './SecureStorageFallbacks';

/**
 * Auth integration configuration
 */
export interface AuthIntegrationConfig {
  // Integration settings
  enableAutoSync: boolean;
  syncOnLogin: boolean;
  syncOnLogout: boolean;
  syncOnTokenRefresh: boolean;
  
  // Token management
  enableTokenValidation: boolean;
  enableAutoRefresh: boolean;
  refreshThreshold: number; // milliseconds
  
  // Permissions integration
  enablePermissionsSync: boolean;
  permissionsCacheKey: string;
  
  // Event handling
  enableEventLogging: boolean;
  enableDebugLogging: boolean;
}

/**
 * Auth integration events
 */
export type AuthIntegrationEvent = 
  | { type: 'auth_tokens_stored'; data: { userId?: string; expiresAt: string } }
  | { type: 'auth_tokens_cleared'; data: { reason: string; timestamp: string } }
  | { type: 'auth_sync_completed'; data: { direction: 'to_storage' | 'from_storage'; duration: number } }
  | { type: 'auth_sync_failed'; data: { error: string; operation: string } }
  | { type: 'token_refresh_triggered'; data: { reason: string; success: boolean } }
  | { type: 'permissions_synced'; data: { permissions: string[]; cached: boolean } };

export type AuthIntegrationEventListener = (event: AuthIntegrationEvent) => void;

/**
 * Auth session data
 */
export interface AuthSessionData {
  user: {
    id: string;
    email: string;
    role?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
    tokenType: string;
  };
  permissions?: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Auth Integration Service
 */
export class AuthIntegrationService {
  private config: AuthIntegrationConfig;
  private eventListeners: Set<AuthIntegrationEventListener> = new Set();
  private isInitialized = false;
  
  // Integration state
  private currentSession: AuthSessionData | null = null;
  private syncInProgress = false;
  private lastSyncTime: string | null = null;

  constructor(config: Partial<AuthIntegrationConfig> = {}) {
    this.config = {
      enableAutoSync: true,
      syncOnLogin: true,
      syncOnLogout: true,
      syncOnTokenRefresh: true,
      enableTokenValidation: true,
      enableAutoRefresh: true,
      refreshThreshold: 300000, // 5 minutes
      enablePermissionsSync: true,
      permissionsCacheKey: 'cached_permissions',
      enableEventLogging: true,
      enableDebugLogging: false,
      ...config
    };
  }

  /**
   * Initialize the auth integration service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (this.config.enableDebugLogging) {
        console.log('🔐 Initializing Auth Integration Service...');
      }

      // Initialize dependencies
      await secureTokenStorage.initialize();
      // await tokenRotationService.initialize(); // DISABLED: Supabase handles token refresh
      await secureStorageFallbacks.initialize();

      // Set up token rotation event listeners
      // DISABLED: Token rotation service not used, Supabase handles refresh
      // if (this.config.enableAutoRefresh) {
      //   this.setupTokenRotationListeners();
      // }

      // Try to restore session from storage
      await this.restoreSessionFromStorage();

      this.isInitialized = true;

      if (this.config.enableDebugLogging) {
        console.log('✅ Auth Integration Service initialized successfully');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Auth Integration Service:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: AuthIntegrationEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: AuthIntegrationEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: AuthIntegrationEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in auth integration event listener:', error);
      }
    });
  }

  /**
   * Handle user login
   */
  async handleLogin(sessionData: AuthSessionData): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      if (this.config.enableDebugLogging) {
        console.log('🔐 Handling user login...');
      }

      // Store session data
      this.currentSession = sessionData;

      // Sync to secure storage if enabled
      if (this.config.syncOnLogin) {
        await this.syncToStorage(sessionData);
      }

      // Cache permissions if enabled
      if (this.config.enablePermissionsSync && sessionData.permissions) {
        await this.cachePermissions(sessionData.permissions);
      }

      if (this.config.enableDebugLogging) {
        console.log('✅ User login handled successfully');
      }
    } catch (error) {
      console.error('❌ Failed to handle login:', error);
      this.emitEvent({
        type: 'auth_sync_failed',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
          operation: 'login'
        }
      });
      throw error;
    }
  }

  /**
   * Handle user logout
   */
  async handleLogout(reason: string = 'user_initiated'): Promise<void> {
    try {
      if (this.config.enableDebugLogging) {
        console.log('🔐 Handling user logout...');
      }

      // Clear secure storage if enabled
      if (this.config.syncOnLogout) {
        await this.clearStoredTokens();
      }

      // Clear permissions cache
      if (this.config.enablePermissionsSync) {
        await this.clearCachedPermissions();
      }

      // Clear current session
      this.currentSession = null;

      this.emitEvent({
        type: 'auth_tokens_cleared',
        data: {
          reason,
          timestamp: new Date().toISOString()
        }
      });

      if (this.config.enableDebugLogging) {
        console.log('✅ User logout handled successfully');
      }
    } catch (error) {
      console.error('❌ Failed to handle logout:', error);
      this.emitEvent({
        type: 'auth_sync_failed',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
          operation: 'logout'
        }
      });
    }
  }

  /**
   * Handle token refresh
   */
  async handleTokenRefresh(newTokens: { accessToken: string; expiresAt: string; refreshToken?: string }): Promise<void> {
    try {
      if (this.config.enableDebugLogging) {
        console.log('🔐 Handling token refresh...');
      }

      // Update current session
      if (this.currentSession) {
        this.currentSession.tokens.accessToken = newTokens.accessToken;
        this.currentSession.tokens.expiresAt = newTokens.expiresAt;
        
        if (newTokens.refreshToken) {
          this.currentSession.tokens.refreshToken = newTokens.refreshToken;
        }
      }

      // Sync to storage if enabled
      if (this.config.syncOnTokenRefresh) {
        if (newTokens.refreshToken) {
          // Full token update
          await secureTokenStorage.storeTokens({
            accessToken: newTokens.accessToken,
            refreshToken: newTokens.refreshToken,
            expiresAt: newTokens.expiresAt,
            tokenType: this.currentSession?.tokens.tokenType || 'Bearer',
            userId: this.currentSession?.user.id,
            createdAt: new Date().toISOString()
          });
        } else {
          // Just update access token
          await secureTokenStorage.updateAccessToken(newTokens.accessToken, newTokens.expiresAt);
        }
      }

      this.emitEvent({
        type: 'token_refresh_triggered',
        data: {
          reason: 'token_refresh',
          success: true
        }
      });

      if (this.config.enableDebugLogging) {
        console.log('✅ Token refresh handled successfully');
      }
    } catch (error) {
      console.error('❌ Failed to handle token refresh:', error);
      this.emitEvent({
        type: 'token_refresh_triggered',
        data: {
          reason: 'token_refresh',
          success: false
        }
      });
      throw error;
    }
  }

  /**
   * Get current session
   */
  getCurrentSession(): AuthSessionData | null {
    return this.currentSession;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    if (this.currentSession) {
      return true;
    }

    // Try to restore from storage
    try {
      const restored = await this.restoreSessionFromStorage();
      return restored !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get valid access token
   */
  async getValidAccessToken(): Promise<string | null> {
    if (!this.currentSession) {
      await this.restoreSessionFromStorage();
    }

    if (!this.currentSession) {
      return null;
    }

    // Check if token needs refresh
    const expiresAt = new Date(this.currentSession.tokens.expiresAt);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();

    if (timeUntilExpiry <= this.config.refreshThreshold) {
      // Try to refresh token
      try {
        const success = await tokenRotationService.refreshTokens('access_token_needed');
        if (success) {
          // Get updated tokens
          const updatedTokens = await secureTokenStorage.retrieveTokens();
          if (updatedTokens) {
            this.currentSession.tokens.accessToken = updatedTokens.accessToken;
            this.currentSession.tokens.expiresAt = updatedTokens.expiresAt;
            return updatedTokens.accessToken;
          }
        }
      } catch (error) {
        if (this.config.enableDebugLogging) {
          console.error('Failed to refresh token:', error);
        }
        return null;
      }
    }

    return this.currentSession.tokens.accessToken;
  }

  /**
   * Get cached permissions
   */
  async getCachedPermissions(): Promise<string[] | null> {
    if (!this.config.enablePermissionsSync) {
      return null;
    }

    try {
      const cached = await secureTokenStorage.getTokenMetadata();
      return cached?.[this.config.permissionsCacheKey] as string[] || null;
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Failed to get cached permissions:', error);
      }
      return null;
    }
  }

  /**
   * Sync session to storage
   */
  private async syncToStorage(sessionData: AuthSessionData): Promise<void> {
    if (this.syncInProgress) return;

    this.syncInProgress = true;
    const startTime = Date.now();

    try {
      const tokenData: StoredTokenData = {
        accessToken: sessionData.tokens.accessToken,
        refreshToken: sessionData.tokens.refreshToken,
        expiresAt: sessionData.tokens.expiresAt,
        tokenType: sessionData.tokens.tokenType,
        userId: sessionData.user.id,
        createdAt: new Date().toISOString()
      };

      await secureTokenStorage.storeTokens(tokenData);

      const duration = Date.now() - startTime;
      this.lastSyncTime = new Date().toISOString();

      this.emitEvent({
        type: 'auth_tokens_stored',
        data: {
          userId: sessionData.user.id,
          expiresAt: sessionData.tokens.expiresAt
        }
      });

      this.emitEvent({
        type: 'auth_sync_completed',
        data: {
          direction: 'to_storage',
          duration
        }
      });
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Restore session from storage
   */
  private async restoreSessionFromStorage(): Promise<AuthSessionData | null> {
    try {
      const tokens = await secureTokenStorage.retrieveTokens();
      
      if (!tokens) {
        return null;
      }

      // Validate tokens
      if (this.config.enableTokenValidation) {
        const validation = await secureTokenStorage.validateTokens();
        if (!validation.isValid) {
          if (this.config.enableDebugLogging) {
            console.warn('Stored tokens are invalid, clearing...');
          }
          await this.clearStoredTokens();
          return null;
        }
      }

      // Restore session data
      const sessionData: AuthSessionData = {
        user: {
          id: tokens.userId || 'unknown',
          email: 'unknown', // Will need to be fetched from API
        },
        tokens: {
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: tokens.expiresAt,
          tokenType: tokens.tokenType
        }
      };

      // Get cached permissions
      if (this.config.enablePermissionsSync) {
        const permissions = await this.getCachedPermissions();
        if (permissions) {
          sessionData.permissions = permissions;
        }
      }

      this.currentSession = sessionData;

      this.emitEvent({
        type: 'auth_sync_completed',
        data: {
          direction: 'from_storage',
          duration: 0
        }
      });

      return sessionData;
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Failed to restore session from storage:', error);
      }
      return null;
    }
  }

  /**
   * Clear stored tokens
   */
  private async clearStoredTokens(): Promise<void> {
    await secureTokenStorage.clearTokens();
    await secureStorageFallbacks.clearAllTokens();
  }

  /**
   * Cache permissions
   */
  private async cachePermissions(permissions: string[]): Promise<void> {
    try {
      // Store permissions in metadata
      const metadata = await secureTokenStorage.getTokenMetadata() || {};
      metadata[this.config.permissionsCacheKey] = permissions;
      
      // Note: This is a simplified approach. In a real implementation,
      // you might want to store permissions separately or extend the token storage
      
      this.emitEvent({
        type: 'permissions_synced',
        data: {
          permissions,
          cached: true
        }
      });
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Failed to cache permissions:', error);
      }
    }
  }

  /**
   * Clear cached permissions
   */
  private async clearCachedPermissions(): Promise<void> {
    try {
      const metadata = await secureTokenStorage.getTokenMetadata() || {};
      delete metadata[this.config.permissionsCacheKey];
      // Note: Simplified implementation
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Failed to clear cached permissions:', error);
      }
    }
  }

  /**
   * Setup token rotation listeners
   * DISABLED: TokenRotationService not used, Supabase handles token refresh
   */
  // private setupTokenRotationListeners(): void {
  //   tokenRotationService.addEventListener((event) => {
  //     switch (event.type) {
  //       case 'refresh_completed':
  //         this.emitEvent({
  //           type: 'token_refresh_triggered',
  //           data: {
  //             reason: 'automatic_refresh',
  //             success: true
  //           }
  //         });
  //         break;

  //       case 'refresh_failed':
  //         this.emitEvent({
  //           type: 'token_refresh_triggered',
  //           data: {
  //             reason: 'automatic_refresh',
  //             success: false
  //           }
  //         });
  //         break;
  //     }
  //   });
  // }

  /**
   * Get integration status
   */
  getStatus(): {
    isInitialized: boolean;
    hasSession: boolean;
    lastSyncTime: string | null;
    syncInProgress: boolean;
  } {
    return {
      isInitialized: this.isInitialized,
      hasSession: this.currentSession !== null,
      lastSyncTime: this.lastSyncTime,
      syncInProgress: this.syncInProgress
    };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AuthIntegrationConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current configuration
   */
  getConfig(): AuthIntegrationConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.eventListeners.clear();
    this.currentSession = null;
    this.isInitialized = false;

    if (this.config.enableDebugLogging) {
      console.log('🧹 Auth Integration Service cleaned up');
    }
  }
}

// Export singleton instance
export const authIntegrationService = new AuthIntegrationService();
